import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/primitives/utils"

/*
 * beacon: no border by default; an empty state sits in the space its content would fill. `bordered`
 * opts back into a plain hairline frame (never dashed).
 */
function Empty({
  className,
  bordered = false,
  ...props
}: React.ComponentProps<"div"> & { bordered?: boolean }) {
  return (
    <div
      data-slot="empty"
      data-bordered={bordered}
      className={cn(
        "flex w-full min-w-0 flex-1 flex-col items-center justify-center gap-5 rounded-lg px-6 py-16 text-center text-balance data-[bordered=true]:border data-[bordered=true]:border-border",
        className,
      )}
      {...props}
    />
  )
}

function EmptyHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-header"
      className={cn("flex max-w-sm flex-col items-center gap-1", className)}
      {...props}
    />
  )
}

const emptyMediaVariants = cva(
  "mb-3 flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      // beacon: the icon sits in a 40px sunken tile with a subtle hairline, in the secondary tier.
      variant: {
        default: "bg-transparent",
        icon: "flex size-10 shrink-0 items-center justify-center rounded-lg bg-surface-sunken text-muted-foreground ring-1 ring-border-subtle [&_svg:not([class*='size-'])]:size-5",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

function EmptyMedia({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof emptyMediaVariants>) {
  return (
    <div
      data-slot="empty-icon"
      data-variant={variant}
      className={cn(emptyMediaVariants({ variant, className }))}
      {...props}
    />
  )
}

function EmptyTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-title"
      // beacon: a title in the type scale.
      className={cn("font-heading text-title font-semibold", className)}
      {...props}
    />
  )
}

function EmptyDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <div
      data-slot="empty-description"
      className={cn(
        // beacon: UI text in the secondary tier, at most 24rem wide; a link in it is brand text.
        "max-w-sm text-ui text-muted-foreground [&>a]:text-brand-text [&>a]:underline-offset-4 [&>a:hover]:underline",
        className,
      )}
      {...props}
    />
  )
}

function EmptyContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-content"
      className={cn(
        // beacon: the actions row.
        "flex w-full max-w-sm min-w-0 flex-row items-center justify-center gap-2 text-ui text-balance",
        className,
      )}
      {...props}
    />
  )
}

export { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent, EmptyMedia }
